// @flow
import I18n from 'i18n-react'

import moment from '../Moment'

export const getOpeningHours = (date: Object) => {
  let timeFrom = null
  let timeTo = null
  switch (date.day()) {
    case 0:
    case 6:
      timeFrom = moment(date)
        .hour(10)
        .minute(0)
        .second(0)
      break
    default:
      timeFrom = moment(date)
        .hour(8)
        .minute(0)
        .second(0)
      break
  }
  switch (date.day()) {
    case 0:
    case 6:
      timeTo = moment(date)
        .hour(18)
        .minute(30)
        .second(0)
      break
    default:
      timeTo = moment(date)
        .hour(21)
        .minute(0)
        .second(0)
  }
  return [timeFrom, timeTo]
}

export const generateTimeRanges = (
  startRange: number,
  endRange: number,
  actualTimes: Object[],
  timesFrom: Object[],
  timesTo: Object[],
  fullRange: boolean = false,
) => {
  for (let i = startRange; i < (fullRange ? endRange : endRange - 1); i++) {
    timesFrom.push(
      moment()
        .hour(i)
        .minute(30)
        .locale('cs')
        .format('HH:mm'),
    )
    timesTo.push(
      moment()
        .hour(i + 1)
        .minute(30)
        .locale('cs')
        .format('HH:mm'),
    )
    actualTimes.push({
      value: i + ':30',
      label: i + ':30' + ' - ' + (i + 1) + ':30',
    })
  }
  return actualTimes
}

export const addFirstThirty = (date: Object, actualTimes: Object[], timesFrom: Object[], timesTo: Object[]) => {
  if (date.minutes() === 0) {
    timesFrom.push(
      moment()
        .hour(date.hours())
        .minute(0)
        .locale('cs')
        .format('HH:mm'),
    )
    timesTo.push(
      moment()
        .hour(date.hours())
        .minute(30)
        .locale('cs')
        .format('HH:mm'),
    )
    actualTimes.push({
      value: date.hours() + ':00',
      label: date.hours() + ':00' + ' - ' + date.hours() + ':30',
    })
  }
  return actualTimes
}

export const addLastThirty = (date: Object, actualTimes: Object[], timesFrom: Object[], timesTo: Object[]) => {
  if (date.minutes() === 0) {
    timesFrom.push(
      moment()
        .hour(date.hours() - 1)
        .minute(30)
        .locale('cs')
        .format('HH:mm'),
    )
    timesTo.push(
      moment()
        .hour(date.hours())
        .minute(0)
        .locale('cs')
        .format('HH:mm'),
    )
    actualTimes.push({
      value: date.hours() - 1 + ':30',
      label: date.hours() - 1 + ':30' + ' - ' + date.hours() + ':00',
    })
  }
  return actualTimes
}

export const generateHoursForDate = (date: Object, dynamicOperatingHours: Object = {}) => {
  let enabledTimesArr = []
  let timeFromArray = []
  let timeToArray = []

  let [timeFrom, timeTo] = getOpeningHours(date)
  const dateFormatted = date.format('YYYY-MM-DD')
  if (dynamicOperatingHours[dateFormatted]) {
    timeFrom = moment(`${dateFormatted} ${dynamicOperatingHours[dateFormatted].timeFrom}`, 'YYYY-MM-DD HH:mm')
    timeTo = moment(`${dateFormatted} ${dynamicOperatingHours[dateFormatted].timeTo}`, 'YYYY-MM-DD HH:mm')
  }
  // today
  if (moment().dayOfYear() === date.dayOfYear()) {
    if (date.hours() < timeFrom.hours()) {
      addFirstThirty(timeFrom, enabledTimesArr, timeFromArray, timeToArray)
      generateTimeRanges(timeFrom.hours(), timeTo.hours(), enabledTimesArr, timeFromArray, timeToArray)
    } else if (date.hours() > timeTo.hours()) {
      // do nothing
    } else {
      // call right now enabled only for actual.hours >= timeFrom.hours
      const rightNowCallTime = moment()
        .locale('cs')
        .format('HH:mm')
      timeFromArray.push(rightNowCallTime)
      timeToArray.push(
        moment()
          .add(10, 'minutes')
          .locale('cs')
          .format('HH:mm'),
      )
      enabledTimesArr.push({
        value: rightNowCallTime,
        label: I18n.translate('cl.general.defaultTime'),
      })
      if (date.minutes() < 30) {
        generateTimeRanges(date.hours(), timeTo.hours(), enabledTimesArr, timeFromArray, timeToArray)
      } else {
        generateTimeRanges(date.hours() + 1, timeTo.hours(), enabledTimesArr, timeFromArray, timeToArray)
      }
    }
    addLastThirty(timeTo, enabledTimesArr, timeFromArray, timeToArray)
  } else {
    addFirstThirty(timeFrom, enabledTimesArr, timeFromArray, timeToArray)
    generateTimeRanges(
      timeFrom.hours(),
      timeTo.hours(),
      enabledTimesArr,
      timeFromArray,
      timeToArray,
      timeTo.minutes() === 30,
    )
    addLastThirty(timeTo, enabledTimesArr, timeFromArray, timeToArray)
  }

  return [enabledTimesArr, timeFromArray, timeToArray]
}

export const getDateToCall = (date?: Object, closed: string[] = [], dynamicOperatingHours: Object = {}) => {
  let dateToCall = date
  if (!date || date.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD')) {
    dateToCall = moment()
  }
  let countLimit = 15
  let count = 1
  let isValidDate = false
  for (let i = 0; i <= countLimit; i++) {
    const [, timeFromArray, timeToArray] = generateHoursForDate(dateToCall, dynamicOperatingHours)
    isValidDate = timeFromArray.reduce((acc, timeFrom, index) => {
      const from = moment(`${dateToCall.format('YYYY-MM-DD')} ${timeFrom}`, 'YYYY-MM-DD HH:mm', true)
      const to = moment(`${dateToCall.format('YYYY-MM-DD')} ${timeToArray[index]}`, 'YYYY-MM-DD HH:mm', true)
      if (
        acc ||
        (!closed.includes(dateToCall.format('YYYY-MM-DD')) &&
          ((index === 0 && dateToCall.isBefore(from)) || dateToCall.isBetween(from, to)))
      ) {
        return true
      }
      return acc
    }, false)
    if (countLimit <= count) {
      isValidDate = true
    }
    if (isValidDate) {
      break
    }
    dateToCall = dateToCall
      .clone()
      .add(24, 'hour')
      .startOf('day')
  }

  return [dateToCall, ...generateHoursForDate(dateToCall, dynamicOperatingHours)]
}
